import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import ArticleAudioButton from '@components/vitals/ArticleAudioButton';
import ArticleAudioProgressBar from '@components/vitals/ArticleAudioProgressBar';
import ArticleAudioTime from '@components/vitals/ArticleAudioTime';

interface Props {
  articleTitle: any;
  activeAudio: string;
}

export default function ArticleAudioBox({ activeAudio, articleTitle }: Props) {
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState<any>(false);

  const duration = audioRef.current && audioRef.current.duration;

  useEffect(() => {
    audioRef.current?.addEventListener(
      'timeupdate',
      () => {
        setCurrentTime(audioRef.current?.currentTime ?? 0);
      },
      { passive: true }
    );
  }, [audioRef, isPlaying]);

  const playAudio = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      audioRef.current?.play();
      setIsPlaying(true);
    }
  };

  return (
    <AudioBoxWrap>
      <AudioBox $activeAudio={activeAudio}>
        <audio ref={audioRef} src={activeAudio} />
        <ArticleAudioButtonContainer onClick={playAudio}>
          <ArticleAudioButton isPlaying={isPlaying} />
        </ArticleAudioButtonContainer>
        <RightSideAudioContainer>
          <StyledAudioTitle>{articleTitle}</StyledAudioTitle>
          <ProgressBarContainer>
            <ArticleAudioProgressBar currentTime={currentTime} duration={duration} audioRef={audioRef} />
          </ProgressBarContainer>
          {currentTime > 0 && (
            <AudioTimeContainer>
              <ArticleAudioTime seconds={currentTime} />
              <ArticleAudioTime seconds={duration} />
            </AudioTimeContainer>
          )}
        </RightSideAudioContainer>
      </AudioBox>
    </AudioBoxWrap>
  );
}

const AudioBoxWrap = styled.div`
  width: 620px;
  max-width: 100%;
  margin-bottom: 30px;

  @media only screen and (max-width: 1300px) {
    padding: 0 40px;
  }
`;

interface AudioBoxProps {
  $activeAudio: any;
}

const AudioBox = styled.div<AudioBoxProps>`
  display: ${({ $activeAudio }) => ($activeAudio ? 'flex' : 'none')};
  margin-top: 40px;
  text-align: left;
  padding: 20px;
  border: solid 2px ${Colors.darkGreen};
  z-index: 10;
  flex-direction: row;
  align-items: center;
  height: 118px;
  position: relative;

  :before {
    border: 2px solid #000;
    content: '';
    position: absolute;
    top: -10px;
    bottom: -10px;
    left: -10px;
    right: -10px;
  }

  @media only screen and (max-width: 1300px) {
    width: 100%;
    margin-top: 0;
    height: auto;
  }
  @media only screen and (max-width: 600px) {
    padding: 10px;
  }
`;

const RightSideAudioContainer = styled.div`
  width: 100%;
  text-align: left;
`;

const ArticleAudioButtonContainer = styled.div`
  z-index: 9;
  height: 100%;
  display: flex;
  align-items: center;
`;

const StyledAudioTitle = styled.p`
  font-size: 16px;
  margin: 15px 0;
`;

const AudioTimeContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const ProgressBarContainer = styled.div``;
